import { mapState } from 'vuex';
import { CardBlock } from '@/cms/definitions/content-types';
import { BasketSettings } from '../BasketSettings';
import InsurancesProductEnum from '@/enums/InsurancesProductEnum';
import FieldValidator from '@/views/calculators/services/fieldValidatorService';
import { BasketProductsEnum } from '@/store/modules/calculatorContext';
import { Options, Vue } from 'vue-class-component';
import store from '@/store/store';
import { cleanInput, jaNejArray, Validator, ValuePair } from '../../services/ValidationService';
import { modalWithExit, togglePopup } from '../../services/AlertService';
import { mapDawaAddress, resetAddress } from '@/views/calculators/services/addressMappingService';
import { cloneDeep } from 'lodash';
import { addCompanyToConsent, hasProductExistingInsurance } from '../../services/ProductService';
import { handleAdditionalStepClick } from '../../services/AdditionalStepService';

@Options({
	name: 'ContactInformationBasketStepComponent',
	components: {},
	computed: mapState<any>({
		basketModel: (state) => state.calculatorContext.basket.model,
		basketCms: (state) => state.calculatorContext.basket.cms,
		consent: (state) => state.consent,
	}),
	props: {
		card: Object, // Card from cms
		isValid: Boolean, // wether card is complete
	},
})
export default class ContactInformationBasketStepComponent extends Vue {
	card: CardBlock; // Card from cms
	isValid: boolean; // wether card is complete

	public basketModel!: any; // data structure for user input (store)
	public basketCms!: BasketSettings; // settings from CMS (store)
	public consent!: { text: string; linkText: string; detailsText: string /*; mandatory: boolean*/ };
	public fieldValidator: FieldValidator = Validator;
	public modelChildren: any = null;
	public modelAdultAccident: Array<any> = [];
	public noEmailCustomer = false;
	public showAddress = false;
	public showConsentDetails = false;

	public jaNejArray: Array<ValuePair> = jaNejArray;
	public skaderOptions = ['0', '1', '2', '3+'];
	public skaderLabels = this.skaderOptions;

	public showAcceptConsent = true;

	public mounted() {
		window.addEventListener('keyup', this.moveOn);

		const basketUnique = store.getters.getSelectableGroupUnique('basket');
		if (basketUnique.includes(InsurancesProductEnum.BOERNE_FAMILIE_FORSIKRING)) {
			const calc = store.getters.getCalculator(BasketProductsEnum.BOERNE_FAMILIE_FORSIKRING0);
			if (calc) {
				this.modelChildren = calc.model;
			}
		} else if (basketUnique.includes(InsurancesProductEnum.BOERNEULYKKES_FORSIKRING)) {
			const calc = store.getters.getCalculator(BasketProductsEnum.BOERNEULYKKES_FORSIKRING0);
			if (calc) {
				this.modelChildren = calc.model;
			}
		}

		if (!this.modelChildren?.readyForBasket) {
			this.modelChildren = undefined;
		}

		if (this.basketModel.contact_information.customerNo) {
			if (!this.fieldValidator.isValidEmail(this.basketModel.contact_information.email)) {
				this.noEmailCustomer = true;
			}
		}
		const tmpAddress = this.basketModel.personInfo.address;
		this.basketModel.personInfo = cloneDeep(store.getters.getCalculatorCommonModel.personInfo);
		if (tmpAddress) {
			this.basketModel.personInfo.address = tmpAddress;
		}
		if (this.basketCms.customerAddressRequired) {
			// DEBUG this.basketModel.personInfo = cloneDeep(store.getters.getCalculatorCommonModel.personInfo);

			this.showAddress = this.basketModel.personInfo.address === undefined;
		}

		const basket = store.getters.getSelectableGroup('basket');
		basket.forEach((product) => {
			if (product.startsWith(InsurancesProductEnum.ULYKKES_FORSIKRING)) {
				const d = store.getters.getCalculator(product)?.model;
				if (d?.readyForBasket) {
					this.modelAdultAccident.push(d);
				}
			}
		});

		// if (!this.consent.mandatory) {
		this.showAcceptConsent = this.setShowAcceptConsent();

		if (this.showAcceptConsent) {
			addCompanyToConsent(this.basketCms, this.basketModel);
		}
		// }
	}

	public beforeUnmount() {
		window.removeEventListener('keyup', this.moveOn);
	}

	public moveOn(evt) {
		if (evt.keyCode === 13 && this.isValid) {
			this.nextStep();
		}
	}

	private setShowAcceptConsent() {
		let existingInsurance = false;
		const basket = store.getters.getSelectableGroup('basket');
		basket.forEach((product) => {
			const calc = store.getters.getCalculator(product);
			if (!existingInsurance && calc?.model?.readyForBasket && calc?.model?.contact_information) {
				existingInsurance = hasProductExistingInsurance(calc.model);
			}
		});
		return existingInsurance;
	}
	public setSubHeader(evt) {
		if (evt.event.type === 'blur') {
			this.basketModel.contact_information.subtitle = cleanInput(evt.value);
		}
	}

	public handleSelection(evt: any) {
		const name = evt.name ? evt.name : evt.id;
		handleAdditionalStepClick(evt.value, name, this.basketModel, this.basketCms, true);
	}

	public checkboxEvent(evt) {
		this.basketModel.contact_information[evt.name] = evt.checked;
	}

	public get showEmail(): boolean {
		if (this.basketModel.contact_information.customerNo) {
			return this.noEmailCustomer;
		}
		return true;
	}

	public get emailFilled(): boolean {
		return this.fieldValidator.isValidEmail(this.basketModel.contact_information.email);
	}

	public get addressFilled(): boolean {
		if (this.showAddress) {
			return this.basketModel.personInfo.address !== undefined;
		}
		return true;
	}

	public addressSearch(address) {
		if (address?.matchText) {
			if (!this.isConsistentAddress(address?.data?.postnr)) {
				this.basketModel.pristineStep.set(this.card.name, false);
				return;
			}
			mapDawaAddress(this.basketModel.personInfo, address);
			this.basketModel.pristineStep.set(this.card.name, true);
		} else {
			resetAddress(this.basketModel.personInfo);
		}
	}

	private isConsistentAddress(newZipCode): boolean {
		const commonPersonInfo = store.getters.getCalculatorCommonModel.personInfo;

		if (!commonPersonInfo.zipCode) {
			return true;
		}

		const ok = newZipCode === commonPersonInfo.zipCode;
		if (!ok) {
			let msg = this.basketCms.wrongAddressPopupContent;
			msg = msg.replace('@zipTxt', commonPersonInfo.zipCode + ' ' + commonPersonInfo.zipName);
			const modal = modalWithExit(this.basketCms);
			modal.title = this.basketCms.wrongAddressPopuptitle;
			modal.content = msg;
			modal.track = true;
			modal.trackToken = 'addressMismatch';
			modal.id = 'addressMismatch';
			togglePopup(this.basketCms, modal);
			resetAddress(this.basketModel.personInfo);

			return false;
		}
		return true;
	}

	public get hasAdultCpr(): boolean {
		const ok = this.modelAdultAccident.length > 1;
		if (!ok && this.modelAdultAccident.length === 1) {
			return this.modelAdultAccident[0].personInfo.otherCustomerAge !== undefined;
		}
		return ok;
	}

	public get skaderOk() {
		return (
			this.basketModel.contact_information.skader &&
			this.basketCms.damagesOk.includes(this.basketModel.contact_information.skader)
		);
	}
	// public isValidAdultCpr(inx) : boolean {
	//     return this.fieldValidator.isValidCpr(this.modelAdultAccident[inx].personInfo.otherCpr);
	// }

	// public isValidChildCpr(inx) : boolean {
	//     return this.fieldValidator.getAgeUnderEighteen(this.modelChildren.familyInfo['cpr' + (inx)]) !== -1;
	// }

	public toggleConsentDetails(evt: Event) {
		evt.preventDefault();
		this.showConsentDetails = !this.showConsentDetails;
	}

	public nextStep() {
		this.$emit('nextStep', this.card.routePath);
	}
}
